import { Inject, Injectable } from '@angular/core';
import { Router, ActivationStart } from '@angular/router';
import { filter, first, map, switchMap, take, throttleTime } from 'rxjs/operators';
import { Observable, BehaviorSubject, interval, of } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { AUTH_SERVER_URL, AUTH_PRODUCT_ID, AUTH_USE_PRODUCT_LOGIN, AUTH_MAIN_ACCOUNT_ID } from './auth.model';

const ACCESS_TOKEN_EXPIRY_STORAGE_KEY = 'access-token-expiry';
const REFRESH_TOKEN_STORAGE_KEY = 'refresh-token';
const REFRESH_TOKEN_EXPIRY_STORAGE_KEY = 'refresh-token-expiry';
const USER_ID_STORAGE_KEY = 'user-id';
const LAST_LOGGED_IN_STORAGE_KEY = 'last-logged-in';
const REFRESH_INTERVAL_MS =240000;
const EXPIRY_REFRESH_WINDOW_MS = 60000;

export class GatewayIdentityUser {
    constructor(
        public userId: number,
        public email: string,
        public fullName: string,
        public roles: string[],
        public claims: string[],
        public token: string,
        public accountId: number,
        public accountName: string
    ) { }

}

export class Credentials {
    constructor(public userId: string, public token: string) {

    }
}

interface AuthTokenResponse {
    token?: string;
    tokenExpiry?: string;
    refreshToken?: string;
    refreshTokenExpiry?: string;
    lastLoggedIn?: string;
}

@Injectable(
    {
        providedIn: 'root',
    }
)
export class AuthService {

    public gatewayIdentityUserObservable: Observable<GatewayIdentityUser>;
    private tokenObservable: Observable<Credentials>;

    private _UserSubject: BehaviorSubject<GatewayIdentityUser>;
    private _TokenSubject: BehaviorSubject<Credentials>;
    private _refreshTokenRequest: Promise<string | null> | null = null;

    constructor(private _router: Router,
        private _httpClient: HttpClient,
        public location: Location,

        @Inject(AUTH_SERVER_URL) public _authServerUrl: string,
        @Inject(AUTH_PRODUCT_ID) private _productCode: string,
        @Inject(AUTH_MAIN_ACCOUNT_ID) private _authMainAccountId: number,
        @Inject(AUTH_USE_PRODUCT_LOGIN) private _authUseProductLogin: boolean) {

        localStorage.removeItem('access-token');

        this._UserSubject = new BehaviorSubject(null);
        this._TokenSubject = new BehaviorSubject(null);

        this.gatewayIdentityUserObservable = this._UserSubject.asObservable();
        this.tokenObservable = this._TokenSubject.asObservable();

        this.monitorForTokenParamAndFetchUser();

        //this.setToken(token);
    }


    private monitorForTokenParamAndFetchUser() {
        this.whenRouterTokenParameterIsSetSaveToken();
        this.whenIsAuthenticatedFetchUser();
        if (!(new URLSearchParams(window.location.search).get('token'))) {
            this.refreshAccessTokenIfAvailable().catch(() => undefined);
        }

        // Refresh before the short-lived access token expires. The interceptor
        // also refreshes/retries immediately when an API call returns 401/403.
        interval(REFRESH_INTERVAL_MS)
            .subscribe(() => {
                this.refreshAccessTokenIfAvailable().catch(() => undefined);
            });
    }


    private setLongToken(val: Credentials): void {
        this._TokenSubject.next(val);
    }

    public getAccessTokenSnapshot(): string | null {
        return this._TokenSubject?.getValue()?.token || null;
    }

    private whenRouterTokenParameterIsSetSaveToken() {
        this._router.events.pipe(filter(e => (e instanceof ActivationStart) && !!e.snapshot.queryParams.token),
            map((e: ActivationStart) => e.snapshot.queryParams), throttleTime(100))
            .subscribe(async p => {
                if (p.key) {
                    console.log('Storing key in local storage:', p.key);
                    localStorage.setItem('key', p.key);
                }
                try {
                    await this.exchangeLoginTokenForSession(p.token, p.userId);
                } catch (error) {
                    console.error('Failed to exchange login token for an application session.', error);
                    this.clearStoredAuth();
                    this.goToLogin(localStorage.getItem('redirect-url') || '/');
                } finally {
                    this.removeLoginTokenFromUrl();
                }
            });
    }

    private sendRemoteLoginRequest(token: string, userId: string): Promise<HttpResponse<AuthTokenResponse>> {
        return this._httpClient.post<AuthTokenResponse>(
            this.getAuthEndpoint(`api/${this._productCode}/login`),
            {
                userId: userId,
                token: token,
                refreshTokenMode: 'body'
            },
            {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
                observe: 'response',
                withCredentials: false
            }).pipe(first()).toPromise();
    }

    private sendRefreshRequest(refreshToken: string): Promise<HttpResponse<AuthTokenResponse>> {
        return this._httpClient.post<AuthTokenResponse>(
            this.getAuthEndpoint(`api/${this._productCode}/refresh`),
            {
                refreshToken: refreshToken
            },
            {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
                observe: 'response',
                withCredentials: false
            }).pipe(first()).toPromise();
    }

    private async exchangeLoginTokenForSession(token: string, userId: string): Promise<string> {
        const result = await this.sendRemoteLoginRequest(token, userId);
        return this.storeAuthResult(result, userId);
    }

    public async ensureAccessToken(forceRefresh: boolean = false): Promise<string | null> {
        const accessToken = this.getAccessTokenSnapshot();
        if (!forceRefresh && accessToken && !this.isStoredExpiryNear(ACCESS_TOKEN_EXPIRY_STORAGE_KEY, EXPIRY_REFRESH_WINDOW_MS)) {
            return accessToken;
        }

        const refreshedAccessToken = await this.refreshAccessToken();
        return refreshedAccessToken || accessToken;
    }

    public async refreshAccessToken(): Promise<string | null> {
        if (this._refreshTokenRequest) {
            return this._refreshTokenRequest;
        }

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        const userId = localStorage.getItem(USER_ID_STORAGE_KEY);

        if (!refreshToken || !userId || this.isStoredExpiryNear(REFRESH_TOKEN_EXPIRY_STORAGE_KEY, 0)) {
            return null;
        }

        this._refreshTokenRequest = this.sendRefreshRequest(refreshToken)
            .then(result => this.storeAuthResult(result, userId))
            .catch(error => {
                console.error('Failed to refresh application access token.', error);
                this.clearStoredAuth();
                return null;
            })
            .finally(() => {
                this._refreshTokenRequest = null;
            });

        return this._refreshTokenRequest;
    }

    private async refreshAccessTokenIfAvailable(): Promise<void> {
        if (!localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)) {
            return;
        }

        await this.refreshAccessToken();
    }

    private storeAuthResult(result: HttpResponse<AuthTokenResponse>, userId: string): string {
        const body = result.body || {};
        const accessToken = body.token || result.headers.get('Authorization');

        if (!accessToken) {
            throw new Error('Auth response did not include an access token.');
        }

        localStorage.setItem(USER_ID_STORAGE_KEY, userId);

        if (body.tokenExpiry) {
            localStorage.setItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY, body.tokenExpiry);
        } else {
            localStorage.removeItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY);
        }

        if (body.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, body.refreshToken);
        } else {
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        }

        if (body.refreshTokenExpiry) {
            localStorage.setItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY, body.refreshTokenExpiry);
        } else {
            localStorage.removeItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY);
        }

        if (body.lastLoggedIn) {
            localStorage.setItem(LAST_LOGGED_IN_STORAGE_KEY, body.lastLoggedIn);
        }

        this.setLongToken(new Credentials(userId, accessToken));
        return accessToken;
    }

    private whenIsAuthenticatedFetchUser() {
        this.tokenObservable.pipe(
            filter(i => i != null)
        ).pipe(
            switchMap(t => this._httpClient.get<any>(`/api/es/v1/user`)),
            filter(result => !!result?.userId),
            map(result => new GatewayIdentityUser(
                result.userId,
                result.email,
                result.fullName,
                result.roles,
                result.claims,
                result.token,
                result.accountId,
                result.accountName
            ))
        ).subscribe(this._UserSubject);
    }


    /**
    * Check the authentication status
    */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._UserSubject.getValue() != null) {
            return of(true);
        }

        if (this._TokenSubject.getValue() != null) {
            return this._UserSubject.asObservable().pipe(filter(i => i != null), take(1), map(x => x != null));
        }

        return of(false);
    }

    public goToLogin(redirectURL: string) {
        localStorage.setItem('redirect-url', redirectURL);

        this.clearStoredAuth();

        const landingUrl = new URL(window.location.origin + '/auth/landing');
        const landingUrlEncoded = encodeURIComponent(landingUrl.toString());
        const casoAuthTokenGenerator = encodeURIComponent(`/api/${this._productCode}/redirect?url=${landingUrlEncoded}`);
        const queryString = this._authUseProductLogin ? this.getProductLoginUrl(casoAuthTokenGenerator) : `?redirect=${casoAuthTokenGenerator}`;
        const authServerUrl = this.getAuthServerUrl();

        if (authServerUrl.endsWith('public/')) {
            window.location.href = '/';
        }
        else {
            window.location.href = `${authServerUrl}auth/login${queryString}`;
        }
    }

    private getProductLoginUrl(redirect: string): string {

        let productLoginUrl = `?productKey=${this._productCode}`;

        if (this._authMainAccountId > 0)
            productLoginUrl += `&accountId=${this._authMainAccountId}`;

        productLoginUrl += `&redirect=${redirect}`;

        return productLoginUrl;
    }

    private getAuthEndpoint(path: string): string {
        return `${this.getAuthServerUrl()}${path.replace(/^\//, '')}`;
    }

    private getAuthServerUrl(): string {
        return this._authServerUrl.endsWith('/') ? this._authServerUrl : `${this._authServerUrl}/`;
    }

    private removeLoginTokenFromUrl(): void {
        window.history.replaceState({}, document.title, window.location.pathname);
    }

    private isStoredExpiryNear(storageKey: string, refreshWindowMs: number): boolean {
        const expiry = localStorage.getItem(storageKey);
        if (!expiry) {
            return false;
        }

        const expiryTime = Date.parse(expiry);
        if (Number.isNaN(expiryTime)) {
            return false;
        }

        return expiryTime <= Date.now() + refreshWindowMs;
    }

    private clearStoredAuth(): void {
        localStorage.removeItem('access-token');
        localStorage.removeItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY);
        localStorage.removeItem(LAST_LOGGED_IN_STORAGE_KEY);
        localStorage.removeItem(USER_ID_STORAGE_KEY);

        if (this._TokenSubject) {
            this._TokenSubject.next(null);
        }
        if (this._UserSubject) {
            this._UserSubject.next(null);
        }
    }

}
